'use client'

import { Spinner, cn } from '@heroui/react'
import { Button } from '@/components/ui/button'
import { ListView } from '@heroui-pro/react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useState } from 'react'

import type { IconName } from '@/config/icons'
import { useNotifications } from '@/lib/services/notifications/use-notifications'
import { Icon } from '@/components/ui/icon'

// Map each notification type to a verified icon name + a tint pair
// (background/foreground). Defaults to a neutral bell for anything unmapped.
// Accent entries use the solid accent/accent-foreground pair (teal pill,
// white glyph, per the teal pairing rule: teal is a fill that carries white
// text, same pattern as account-mobile-tabs), never the accent-soft pair:
// the soft mix needs re-verification against the new teal palette before
// it can be trusted for WCAG AA. The warning/danger/success soft chips are
// unaffected by this and keep the soft token pair.
const TYPE_VISUAL: Record<string, { icon: IconName; tint: string }> = {
  SYSTEM: { icon: 'server', tint: 'bg-surface text-muted' },
  UPDATE: { icon: 'refresh', tint: 'bg-surface text-muted' },
  SECURITY: { icon: 'shield', tint: 'bg-warning-soft text-warning-soft-foreground' },
  ALERT: { icon: 'bell', tint: 'bg-danger-soft text-danger-soft-foreground' },
  APPOINTMENT: { icon: 'calendar', tint: 'bg-accent text-accent-foreground' },
  MESSAGE: { icon: 'message', tint: 'bg-accent text-accent-foreground' },
  MENTION: { icon: 'user', tint: 'bg-accent text-accent-foreground' },
  PAYMENT: { icon: 'credit-card', tint: 'bg-success-soft text-success-soft-foreground' },
  MARKETING: { icon: 'flag', tint: 'bg-success-soft text-success-soft-foreground' },
  INTERACTION: { icon: 'heart', tint: 'bg-danger-soft text-danger-soft-foreground' },
  INTERACTION_FAVOURITE: { icon: 'heart', tint: 'bg-danger-soft text-danger-soft-foreground' },
  INTERACTION_COMMENT: { icon: 'comments', tint: 'bg-accent text-accent-foreground' },
  INTERACTION_RATING: { icon: 'star', tint: 'bg-warning-soft text-warning-soft-foreground' },
  INTERACTION_SHARE: { icon: 'share', tint: 'bg-success-soft text-success-soft-foreground' },
  INTERACTION_MENTION: { icon: 'user', tint: 'bg-accent text-accent-foreground' },
  INTERACTION_DOWNLOAD: { icon: 'download', tint: 'bg-surface text-muted' },
  INTERACTION_PURCHASE: { icon: 'cart-4', tint: 'bg-success-soft text-success-soft-foreground' },
}

function visualFor(type: string) {
  return TYPE_VISUAL[type] ?? { icon: 'notification' as IconName, tint: 'bg-surface text-muted' }
}

// "System", "Message", "Favourite" — humanize the enum for a compact meta label.
function humanizeType(type: string): string {
  const base = type.startsWith('INTERACTION_') ? type.slice('INTERACTION_'.length) : type
  return base.charAt(0).toUpperCase() + base.slice(1).toLowerCase()
}

// Dependency-free relative time good enough for a dropdown ("2h ago", "Mar 4").
function relativeTime(value?: string | Date): string {
  if (!value) return ''
  const then = new Date(value).getTime()
  if (Number.isNaN(then)) return ''
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return new Date(value).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })
}

export function NotificationsList() {
  const router = useRouter()
  const {
    notifications,
    loading,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    deleteNotification,
    deleteAllNotifications,
  } = useNotifications()

  const [isMarkingAll, setIsMarkingAll] = useState(false)
  const [isClearing, setIsClearing] = useState(false)

  const items = (notifications ?? []).filter((n) => !(n as { archived?: boolean }).archived)
  const hasUnread = items.some((n) => !n.read)

  const handleAction = (id: string) => {
    const item = items.find((n) => n.id === id)
    if (!item) return
    if (!item.read) void markNotificationAsRead(id)
    const url = (item.data as { actionUrl?: string } | undefined)?.actionUrl
    if (url && url !== '#') router.push(url)
  }

  const handleMarkAll = async () => {
    setIsMarkingAll(true)
    try {
      await markAllNotificationsAsRead()
    } finally {
      setIsMarkingAll(false)
    }
  }

  const handleClearAll = async () => {
    setIsClearing(true)
    try {
      await deleteAllNotifications()
    } finally {
      setIsClearing(false)
    }
  }

  if (loading && items.length === 0) {
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 p-8 text-center text-muted">
        <Icon icon="notification" className="text-3xl" />
        <p className="text-sm">No notifications yet</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      {/* Action bar */}
      <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-border bg-surface/95 px-3 py-2 backdrop-blur">
        <Button
          size="sm"
          variant="ghost"
          isDisabled={!hasUnread || isMarkingAll}
          isLoading={isMarkingAll}
          onPress={handleMarkAll}
        >
          <Icon icon="check" className="text-base" aria-hidden="true" /> Mark all read
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="text-danger"
          isDisabled={isClearing}
          isLoading={isClearing}
          onPress={handleClearAll}
        >
          <Icon icon="trash" className="text-base" aria-hidden="true" /> Clear all
        </Button>
      </div>

      <ListView
        aria-label="Notifications"
        variant="secondary"
        selectionMode="none"
        items={items}
        onAction={(key) => handleAction(String(key))}
      >
        {(item) => {
          const visual = visualFor(item.type)
          const createdAt = (item as { createdAt?: string | Date }).createdAt
          return (
            <ListView.Item id={item.id} textValue={item.title}>
              <ListView.ItemContent>
                <div
                  className={cn(
                    'flex size-10 flex-none items-center justify-center rounded-full',
                    visual.tint,
                  )}
                >
                  <Icon icon={visual.icon} className="text-lg" aria-hidden="true" />
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <ListView.Title className={cn('truncate', !item.read && 'font-semibold')}>
                    {item.title}
                  </ListView.Title>
                  <ListView.Description className="line-clamp-2 whitespace-normal">
                    {item.message}
                  </ListView.Description>
                  <span className="text-[11px] text-muted">
                    {humanizeType(item.type)}
                    {createdAt ? ` · ${relativeTime(createdAt)}` : ''}
                  </span>
                </div>
              </ListView.ItemContent>
              <ListView.ItemAction>
                <div className="flex items-center gap-1.5">
                  {!item.read && (
                    <span className="size-2 flex-none rounded-full bg-accent" aria-label="Unread" />
                  )}
                  <Button
                    isIconOnly
                    size="sm"
                    variant="ghost"
                    aria-label="Delete notification"
                    onPress={() => void deleteNotification(item.id)}
                  >
                    <Icon icon="close-circle" className="text-base text-muted" aria-hidden="true" />
                  </Button>
                </div>
              </ListView.ItemAction>
            </ListView.Item>
          )
        }}
      </ListView>

      <div className="border-t border-border p-2 text-center">
        <Link href="/notifications">
          <Button size="sm" variant="ghost" className="text-xs">
            View all notifications
          </Button>
        </Link>
      </div>
    </div>
  )
}
